<template>
  <v-dialog v-model="dialogVisible" persistent max-width="500px">
    <v-card>
      <v-card-title class="text-h5 pa-4 pb-0">
        <v-icon left color="warning" class="mr-2">mdi-lock-reset</v-icon>
        Change Your Password
      </v-card-title>
      
      <v-card-text class="pa-4">
        <p class="text-body-2 mb-4">
          For security reasons, you must change your temporary password to continue.
        </p>
        
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-text-field
            v-model="newPassword"
            label="New Password"
            type="password"
            variant="solo"
            rounded
            flat
            bg-color="#f5f5f5"
            prepend-inner-icon="mdi-lock"
            :rules="passwordRules"
            class="mb-3"
            @input="validateNewPassword"
          />
          
          <v-text-field
            v-model="confirmPassword"
            label="Confirm New Password"
            type="password"
            variant="solo"
            rounded
            flat
            bg-color="#f5f5f5"
            prepend-inner-icon="mdi-lock-check"
            :rules="confirmPasswordRules"
            class="mb-3"
          />
          
          <div v-if="passwordStrength" class="password-strength mb-3">
            <div class="d-flex align-center mb-1">
              <span class="text-caption mr-2">Password Strength:</span>
              <v-progress-linear
                :value="passwordStrength.score"
                :color="passwordStrength.color"
                height="8"
                rounded
                class="flex-grow-1"
              ></v-progress-linear>
            </div>
            <span class="text-caption" :class="passwordStrength.color + '--text'">
              {{ passwordStrength.message }}
            </span>
          </div>
        </v-form>
      </v-card-text>
      
      <v-card-actions class="pa-4 pt-0">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!valid || loading"
          :loading="loading"
          @click="changePassword"
        >
          <v-icon left>mdi-check</v-icon>
          Change Password
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { validatePassword } from '../utils/passwordGenerator';

export default {
  name: 'PasswordResetDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'password-changed'],
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    }
  },
  data() {
    return {
      valid: false,
      loading: false,
      newPassword: '',
      confirmPassword: '',
      passwordStrength: null,
      passwordRules: [
        v => !!v || 'New password is required',
        v => v.length >= 8 || 'Password must be at least 8 characters',
        v => /(?=.*[a-z])/.test(v) || 'Must contain at least one lowercase letter',
        v => /(?=.*[A-Z])/.test(v) || 'Must contain at least one uppercase letter',
        v => /(?=.*\d)/.test(v) || 'Must contain at least one number'
      ],
      confirmPasswordRules: [
        v => !!v || 'Please confirm your password',
        v => v === this.newPassword || 'Passwords must match'
      ]
    };
  },
  methods: {
    validateNewPassword() {
      if (this.newPassword) {
        const validation = validatePassword(this.newPassword);
        this.passwordStrength = {
          score: this.calculatePasswordScore(this.newPassword),
          color: validation.isValid ? 'success' : 'warning',
          message: validation.message
        };
      } else {
        this.passwordStrength = null;
      }
    },
    
    calculatePasswordScore(password) {
      let score = 0;
      
      // Length contribution
      if (password.length >= 8) score += 20;
      if (password.length >= 12) score += 10;
      
      // Character variety contribution
      if (/[a-z]/.test(password)) score += 20;
      if (/[A-Z]/.test(password)) score += 20;
      if (/\d/.test(password)) score += 20;
      if (/[^a-zA-Z0-9]/.test(password)) score += 10;
      
      return Math.min(score, 100);
    },
    
    async changePassword() {
      if (!this.$refs.form.validate()) return;
      
      this.loading = true;
      
      try {
        // Emit the password change event
        this.$emit('password-changed', {
          newPassword: this.newPassword,
          confirmPassword: this.confirmPassword
        });
        
        // Close the dialog
        this.$emit('update:visible', false);
        
        // Reset form
        this.resetForm();
        
      } catch (error) {
        console.error('Error changing password:', error);
      } finally {
        this.loading = false;
      }
    },
    
    resetForm() {
      this.newPassword = '';
      this.confirmPassword = '';
      this.passwordStrength = null;
      this.$refs.form.resetValidation();
    }
  },
  
  watch: {
    visible(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    }
  }
};
</script>

<style scoped>
.password-strength {
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #dee2e6;
}
</style> 